import { useEffect, useRef, useState } from "react";
import { t } from "../utils/i18n";
import { getKebabPrefs, saveKebabPrefs } from "../utils/auth";
import { getPrice } from "../utils/pricing";
import { getCachedImageUrl } from "../utils/imageCache";

const SIZE_OPTIONS = [
  { value: "Mazas", labelKey: "size_mazas" },
  { value: "Didelis", labelKey: "size_didelis" },
];

const TYPE_OPTIONS = [
  { value: "Todzes", labelKey: "type_todzes" },
  { value: "Paprastas", labelKey: "type_paprastas" },
];

const TYPE_PREVIEW = {
  Todzes: "https://assets.kebatic.org/todzesKebabas.jpeg",
  Paprastas: "https://assets.kebatic.org/paprastasKebabas.jpeg",
};

const PREVIEW_HOVER_DELAY_MS = 650;

const SAUCE_OPTIONS = [
  { value: "Pikantiskas", labelKey: "sauce_pikantiskas" },
  { value: "Cesnakinis", labelKey: "sauce_cesnakinis" },
  { value: "Astrus", labelKey: "sauce_astrus" },
  { value: "Mixas", labelKey: "sauce_mixas" },
];

const INDIVIDUAL_SAUCES = ["Pikantiskas", "Cesnakinis", "Astrus"];

const normalizeSauces = sauces => {
  if (sauces.includes("Mixas")) return ["Mixas"];
  const selected = INDIVIDUAL_SAUCES.filter(s => sauces.includes(s));
  if (selected.length === INDIVIDUAL_SAUCES.length) return ["Mixas"];
  return selected;
};

const pickRandom = items => items[Math.floor(Math.random() * items.length)];

const randomSauces = () => {
  if (Math.random() < 0.35) return ["Mixas"];
  const count = 1 + Math.floor(Math.random() * 2);
  return [...INDIVIDUAL_SAUCES].sort(() => Math.random() - 0.5).slice(0, count);
};

export const OrderForm = ({ onSubmit, lang = "lt", userName = "" }) => {
  const savedPrefs = getKebabPrefs(userName);
  const [name, setName] = useState(userName);
  const [size, setSize] = useState(savedPrefs?.size || "");
  const [type, setType] = useState(savedPrefs?.type || "");
  const [sauces, setSauces] = useState(savedPrefs?.sauces || []);
  const [previewType, setPreviewType] = useState(null);
  const [previewUrls, setPreviewUrls] = useState(TYPE_PREVIEW);
  const previewTimerRef = useRef(null);

  const clearPreviewTimer = () => {
    if (previewTimerRef.current) {
      clearTimeout(previewTimerRef.current);
      previewTimerRef.current = null;
    }
  };

  const showTypePreview = value => {
    clearPreviewTimer();
    previewTimerRef.current = setTimeout(() => {
      setPreviewType(value);
    }, PREVIEW_HOVER_DELAY_MS);
  };

  const hideTypePreview = () => {
    clearPreviewTimer();
    setPreviewType(null);
  };

  useEffect(() => () => clearPreviewTimer(), []);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      const entries = Object.entries(TYPE_PREVIEW);
      const next = {};
      await Promise.all(
        entries.map(async ([key, url]) => {
          try {
            next[key] = await getCachedImageUrl(url);
          } catch {
            next[key] = url;
          }
        })
      );
      if (!cancelled) setPreviewUrls(next);
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const handleSubmit = e => {
    e.preventDefault();
    if (name.trim().toLowerCase().includes("sik")) {
      alert("Šitas vardas neleidžiamas – no tarzans here!");
      return;
    }
    const normalizedSauces = normalizeSauces(sauces);
    saveKebabPrefs(userName || name, { size, type, sauces: normalizedSauces });
    onSubmit({ name, size, type, sauces: normalizedSauces.sort().join(", ") });
    setName(userName);
    const prefs = getKebabPrefs(userName || name);
    setSize(prefs?.size || "");
    setType(prefs?.type || "");
    setSauces(prefs?.sauces || []);
  };

  const toggleSauce = value => {
    setSauces(prev => {
      if (value === "Mixas") {
        return prev.includes("Mixas") ? [] : ["Mixas"];
      }

      const withoutMix = prev.filter(s => s !== "Mixas");
      const next = withoutMix.includes(value)
        ? withoutMix.filter(s => s !== value)
        : [...withoutMix, value];

      return normalizeSauces(next);
    });
  };

  const randomizeKebab = () => {
    if (!name.trim()) return;
    setSize(pickRandom(SIZE_OPTIONS).value);
    setType(pickRandom(TYPE_OPTIONS).value);
    setSauces(randomSauces());
  };

  const estimatedPrice =
    size && type && sauces.length > 0 ? getPrice(size, type) : 0;

  return (
    <section className="card">
      <form onSubmit={handleSubmit}>
        <div className="form-field">
          <label className="field-label" data-i18n="name_label">Vardas:</label>
          <div className="name-row">
            <input
              type="text"
              value={name}
              required
              placeholder={t(lang, "name_placeholder")}
              onChange={e => setName(e.target.value)}
            />
            <button
              type="button"
              className="btn-randomize"
              disabled={!name.trim()}
              title={t(lang, "randomize_title")}
              aria-label={t(lang, "randomize_title")}
              onClick={randomizeKebab}
            >
              {t(lang, "randomize_btn")}
            </button>
          </div>
        </div>

        <div className="form-field">
          <label className="field-label" data-i18n="size_label">Dydis:</label>
          <div className="options">
            {SIZE_OPTIONS.map(({ value, labelKey }) => (
              <label key={value} className="chip-option">
                <input
                  type="radio"
                  name="size"
                  value={value}
                  checked={size === value}
                  onChange={e => setSize(e.target.value)}
                />
                {t(lang, labelKey)}
              </label>
            ))}
          </div>
        </div>

        <div className="form-field">
          <label className="field-label" data-i18n="type_label">Todzes / Paprastas:</label>
          <div className="options">
            {TYPE_OPTIONS.map(({ value, labelKey }) => (
              <label
                key={value}
                className="chip-option chip-option--preview"
                onMouseEnter={() => showTypePreview(value)}
                onMouseLeave={hideTypePreview}
                onFocus={() => showTypePreview(value)}
                onBlur={hideTypePreview}
              >
                <input
                  type="radio"
                  name="type"
                  value={value}
                  checked={type === value}
                  onChange={e => setType(e.target.value)}
                />
                {t(lang, labelKey)}
                {previewType === value && (
                  <span className="type-preview" role="tooltip">
                    <img
                      src={previewUrls[value]}
                      alt={t(lang, labelKey)}
                      className="type-preview-img"
                    />
                  </span>
                )}
              </label>
            ))}
          </div>
        </div>

        <div className="form-field">
          <label className="field-label" data-i18n="sauce_label">Padazas:</label>
          <div className="options">
            {SAUCE_OPTIONS.map(({ value, labelKey }) => (
              <label key={value} className="chip-option">
                <input
                  type="checkbox"
                  value={value}
                  checked={sauces.includes(value)}
                  onChange={() => toggleSauce(value)}
                />
                {t(lang, labelKey)}
              </label>
            ))}
          </div>
        </div>

        <button type="submit">
          {t(lang, "submit_btn")}
          {estimatedPrice > 0 && (
            <span className="submit-price"> · {estimatedPrice.toFixed(2)} EUR</span>
          )}
        </button>
      </form>
    </section>
  );
};

export default OrderForm;
